import React, { useState, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Trash2, 
  Lock, 
  Unlock,
  Maximize2,
  Square,
  Layers,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

interface ImageToolbarProps {
  editor: Editor;
}

const ImageToolbar: React.FC<ImageToolbarProps> = ({ editor }) => {
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [aspectRatioLocked, setAspectRatioLocked] = useState(true);
  const [originalAspectRatio, setOriginalAspectRatio] = useState<number | null>(null);

  const getImageAttributes = useCallback(() => {
    const { selection } = editor.state;
    const node = editor.state.doc.nodeAt(selection.from);
    if (node?.type.name === 'image') {
      return node.attrs;
    }
    return null;
  }, [editor]);

  useEffect(() => {
    const attrs = getImageAttributes();
    if (attrs) {
      const w = attrs.width || '';
      const h = attrs.height || '';
      setWidth(w.toString());
      setHeight(h.toString());
      
      if (w && h && !originalAspectRatio) {
        setOriginalAspectRatio(Number(w) / Number(h));
      }
    }
  }, [editor.state.selection, getImageAttributes, originalAspectRatio]);

  const handleWidthChange = (newWidth: string) => {
    setWidth(newWidth);
    if (aspectRatioLocked && originalAspectRatio && newWidth) {
      const calculatedHeight = Math.round(Number(newWidth) / originalAspectRatio);
      setHeight(calculatedHeight.toString());
    }
  };

  const handleHeightChange = (newHeight: string) => {
    setHeight(newHeight);
    if (aspectRatioLocked && originalAspectRatio && newHeight) {
      const calculatedWidth = Math.round(Number(newHeight) * originalAspectRatio);
      setWidth(calculatedWidth.toString());
    }
  };

  const applySize = () => {
    if (width || height) {
      editor.chain().focus().updateAttributes('image', {
        width: width ? Number(width) : null,
        height: height ? Number(height) : null,
      }).run();
    }
  };

  const setFloat = (float: 'none' | 'left' | 'right') => {
    editor.chain().focus().updateAttributes('image', { 
      float,
      display: 'inline',
      zIndex: 'auto',
      position: 'relative',
    }).run();
  };

  const setCentered = () => {
    editor.chain().focus().updateAttributes('image', { 
      float: 'none',
      display: 'block',
      zIndex: 'auto',
      position: 'relative',
    }).run();
  };

  const setLayering = (zIndex: string) => {
    editor.chain().focus().updateAttributes('image', { 
      zIndex,
      position: 'relative',
    }).run();
  };

  const deleteImage = () => {
    editor.chain().focus().deleteSelection().run();
  };

  const presetSizes = [50, 100, 150, 200, 300];

  const attrs = getImageAttributes();
  const currentFloat = attrs?.float || 'none';
  const currentDisplay = attrs?.display || 'inline';
  const currentZIndex = attrs?.zIndex || 'auto';

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 p-1 bg-popover border rounded-lg shadow-lg">
        {/* Size Controls */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 px-2">
              <Maximize2 className="h-4 w-4 mr-1" />
              Size
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Dimensions</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAspectRatioLocked(!aspectRatioLocked)}
                  className="h-6 px-2"
                >
                  {aspectRatioLocked ? (
                    <Lock className="h-3 w-3" />
                  ) : (
                    <Unlock className="h-3 w-3" />
                  )}
                </Button>
              </div>
              
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Width</Label>
                  <Input
                    type="number"
                    value={width}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    onBlur={applySize}
                    onKeyDown={(e) => e.key === 'Enter' && applySize()}
                    placeholder="auto"
                    className="h-8"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Height</Label>
                  <Input
                    type="number"
                    value={height}
                    onChange={(e) => handleHeightChange(e.target.value)}
                    onBlur={applySize}
                    onKeyDown={(e) => e.key === 'Enter' && applySize()}
                    placeholder="auto"
                    className="h-8"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Quick sizes (width)</Label>
                <div className="flex flex-wrap gap-1">
                  {presetSizes.map((size) => (
                    <Button
                      key={size}
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        const newHeight = aspectRatioLocked && originalAspectRatio 
                          ? Math.round(size / originalAspectRatio) 
                          : null;
                        editor.chain().focus().updateAttributes('image', { 
                          width: size, 
                          height: newHeight 
                        }).run();
                        setWidth(size.toString());
                        if (newHeight) setHeight(newHeight.toString());
                      }}
                    >
                      {size}px
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="h-6" />

        {/* Alignment/Float Controls */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={currentFloat === 'left' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setFloat('left')}
            >
              <AlignLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Float Left (text wraps right)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={currentDisplay === 'block' && currentFloat === 'none' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={setCentered}
            >
              <AlignCenter className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Center (text above/below)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={currentFloat === 'right' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setFloat('right')}
            >
              <AlignRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Float Right (text wraps left)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={currentDisplay === 'inline' && currentFloat === 'none' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setFloat('none')}
            >
              <Square className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Inline (flows with text)</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6" />

        {/* Layering Controls */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 px-2">
              <Layers className="h-4 w-4 mr-1" />
              Layer
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48" align="start">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Text Layering</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Note: May not work in all email clients.
              </p>
              <Button
                variant={currentZIndex === '-1' ? 'secondary' : 'ghost'}
                size="sm"
                className="w-full justify-start"
                onClick={() => setLayering('-1')}
              >
                <ArrowDown className="h-4 w-4 mr-2" />
                Behind Text
              </Button>
              <Button
                variant={currentZIndex === 'auto' ? 'secondary' : 'ghost'}
                size="sm"
                className="w-full justify-start"
                onClick={() => setLayering('auto')}
              >
                <Layers className="h-4 w-4 mr-2" />
                Normal
              </Button>
              <Button
                variant={currentZIndex === '10' ? 'secondary' : 'ghost'}
                size="sm"
                className="w-full justify-start"
                onClick={() => setLayering('10')}
              >
                <ArrowUp className="h-4 w-4 mr-2" />
                In Front of Text
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="h-6" />

        {/* Delete */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              onClick={deleteImage}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete Image</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default ImageToolbar;
